import type { SingleValue, StylesConfig } from 'react-select';

import { Text, Select as UISelect } from '@revelio/ui';

import styles from './filterSelectTheme.module.css';
import themedStyles from './NotToggle.module.css';

interface NotToggleOption {
  label: string;
  displayLabel: string;
  value: boolean;
  showDefault?: boolean;
}

interface NotToggleProps {
  filterName: string;
  isNot: boolean;
  onChange: (isNot: boolean) => void;
}

const options: NotToggleOption[] = [
  {
    label: 'Is any of',
    displayLabel: 'is any',
    value: false,
    showDefault: true,
  },
  { label: 'Is not', displayLabel: 'is not', value: true },
];

// Compact, transparent control that sits inline in the filter form; the menu,
// options and chevron pick up the @revelio/ui Select's own themed tokens.
const themedSelectStyles: StylesConfig<NotToggleOption, false> = {
  control: (base) => ({
    ...base,
    padding: 0,
    gap: '2px',
    border: 'none',
    background: 'transparent',
    boxShadow: 'none',
    cursor: 'pointer',
  }),
  singleValue: (base) => ({
    ...base,
    margin: 0,
    fontSize: '12px',
    fontWeight: 600,
    color: 'var(--ui-text-secondary)',
  }),
  dropdownIndicator: (base) => ({
    ...base,
    padding: 0,
    color: 'var(--ui-text-secondary)',
  }),
  // Legacy-compact dropdown on the shared modal surface: keep the @revelio/ui
  // menu chrome (border/shadow/font/colors) but pull the list background to the
  // modal token and drop the option rows a line-height smaller.
  menu: (base) => ({
    ...base,
    minWidth: '160px',
    width: 'max-content',
    background: 'var(--ui-surface-floating)',
  }),
  menuList: (base) => ({ ...base, padding: 'var(--ui-spacing-1) 0' }),
  option: (base) => ({
    ...base,
    padding: 'var(--ui-spacing-1) var(--ui-spacing-3)',
    lineHeight: 'var(--ui-line-height-md)',
  }),
};

const renderThemedOptionLabel = (
  { label, displayLabel, showDefault }: NotToggleOption,
  { context }: { context: 'menu' | 'value' }
) => {
  if (context === 'value') {
    return displayLabel;
  }
  return (
    <span className={themedStyles.optionRow}>
      <span>{label}</span>
      {showDefault && <span className={themedStyles.defaultTag}>Default</span>}
    </span>
  );
};

export const NotToggle: React.FC<NotToggleProps> = ({
  filterName,
  isNot,
  onChange,
}) => {
  const handleChange = (selected: SingleValue<NotToggleOption>) => {
    if (selected) {
      onChange(selected.value);
    }
  };

  const selectedOption =
    options.find((opt) => opt.value === isNot) ?? options[0];

  return (
    <div className={themedStyles.toggle}>
      <Text fontSize="x-sm" weight="semibold" className={styles.notLabel}>
        {filterName}
      </Text>
      <Text fontSize="x-sm" weight="semibold" className={styles.notLabel}>
        ·
      </Text>
      <UISelect<NotToggleOption>
        aria-label={`${filterName} operator`}
        className={themedStyles.notSelect}
        classNamePrefix="not-toggle-select"
        options={options}
        value={selectedOption}
        onChange={handleChange}
        isSearchable={false}
        isClearable={false}
        formatOptionLabel={renderThemedOptionLabel}
        styles={themedSelectStyles}
      />
    </div>
  );
};
